import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { clientAPI, productAPI } from "@/services/api";
import {
  CheckCircle2,
  Clock,
  Download,
  FileText,
  Package,
  RefreshCw,
  Search,
  Users,
  XCircle,
} from "lucide-react";
import {
  TailAdminDataTable,
  TailAdminEmptyState,
  TailAdminMetricCard,
  TailAdminPageHeader,
  TailAdminPanel,
  TailAdminStatusBadge,
} from "@/components/template/tailadmin/DashboardPrimitives";

const getProductId = (product) => product?.productId || product?.id || product?._id || "";
const safeText = (value) => String(value || "");

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-NG", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const statusTone = {
  verified: "success",
  paid: "success",
  pending: "warning",
  rejected: "danger",
};

const statusIcon = {
  verified: CheckCircle2,
  paid: CheckCircle2,
  pending: Clock,
  rejected: XCircle,
};

const ClientEnrollments = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [productFilter, setProductFilter] = useState("all");

  const fetchEnrollments = useCallback(async () => {
    setLoading(true);
    try {
      const status = statusFilter === "all" ? null : statusFilter;
      const productId = productFilter === "all" ? null : productFilter;
      const [enrollmentsRes, productsRes] = await Promise.all([
        clientAPI.getEnrollments(status, null, productId),
        productAPI.getProducts(),
      ]);
      setEnrollments(Array.isArray(enrollmentsRes.data) ? enrollmentsRes.data : enrollmentsRes.data?.items || []);
      setProducts(Array.isArray(productsRes.data) ? productsRes.data : productsRes.data?.products || []);
    } catch {
      toast.error("Failed to load enrollments");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, productFilter]);

  useEffect(() => {
    fetchEnrollments();
  }, [fetchEnrollments]);

  const filteredEnrollments = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return enrollments;

    return enrollments.filter((enrollment) =>
      [
        enrollment.phone_masked,
        enrollment.phone,
        enrollment.brm_name,
        enrollment.product_name,
        enrollment.status,
      ].map(safeText).some((value) => value.toLowerCase().includes(query))
    );
  }, [enrollments, search]);

  const summary = useMemo(() => ({
    active: enrollments.filter((item) => ["verified", "paid"].includes(item.status)).length,
    pending: enrollments.filter((item) => item.status === "pending").length,
    churned: enrollments.filter((item) => item.status === "rejected").length,
    brms: new Set(enrollments.map((item) => item.brm_id || item.brm_name).filter(Boolean)).size,
  }), [enrollments]);

  const handleExport = () => {
    const csvContent = [
      ["Phone", "Product", "BRM", "Status", "Enrolled At", "Subscribed At"].join(","),
      ...enrollments.map((item) => [
        item.phone_masked || item.phone,
        item.product_name,
        item.brm_name,
        item.status,
        item.enrolled_at,
        item.subscribed_at || "",
      ].map((value) => `"${safeText(value).replace(/"/g, '""')}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `enrollments-export-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Export downloaded");
  };

  const rows = filteredEnrollments.map((enrollment) => {
    const StatusIcon = statusIcon[enrollment.status] || FileText;

    return {
      id: enrollment.id || `${enrollment.phone_masked}-${enrollment.enrolled_at}`,
      cells: [
        <span className="font-semibold tabular-nums text-gray-900">{enrollment.phone_masked || enrollment.phone || "Unknown"}</span>,
        <span className="font-medium text-gray-700">{enrollment.product_name || "No product"}</span>,
        <span className="text-gray-600">{enrollment.brm_name || "Unassigned"}</span>,
        <TailAdminStatusBadge tone={statusTone[enrollment.status] || "neutral"}>
          <span className="inline-flex items-center gap-1">
            <StatusIcon className="h-3.5 w-3.5" />
            {enrollment.status || "unknown"}
          </span>
        </TailAdminStatusBadge>,
        <span className="text-gray-500">{formatDate(enrollment.enrolled_at)}</span>,
        <span className="text-gray-500">{formatDate(enrollment.subscribed_at)}</span>,
      ],
    };
  });

  return (
    <div className="space-y-6">
      <TailAdminPageHeader
        eyebrow="Proof tracking"
        title="Enrollments"
        description="Review user enrollments, activation state, BRM source, and subscription proof status."
        actions={
          <>
            <Button variant="outline" onClick={handleExport} disabled={loading || enrollments.length === 0} data-testid="export-enrollments-btn">
              <Download className="h-4 w-4" />
              Export
            </Button>
            <Button variant="outline" onClick={fetchEnrollments} disabled={loading} data-testid="refresh-enrollments-btn">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </>
        }
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <TailAdminMetricCard label="Total enrollments" value={enrollments.length} icon={FileText} tone="primary" helper="All records" loading={loading} />
        <TailAdminMetricCard label="Active" value={summary.active} icon={CheckCircle2} tone="success" helper="Verified users" loading={loading} />
        <TailAdminMetricCard label="Pending" value={summary.pending} icon={Clock} tone="warning" helper="Awaiting proof" loading={loading} />
        <TailAdminMetricCard label="Source BRMs" value={summary.brms} icon={Users} tone="neutral" helper="Contributors" loading={loading} />
      </div>

      <TailAdminPanel
        title="Enrollment ledger"
        description={`${filteredEnrollments.length} of ${enrollments.length} enrollments shown`}
        action={
          <div className="flex w-full flex-col gap-3 lg:w-auto lg:flex-row">
            <div className="relative w-full lg:w-80">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search phone, BRM, or product..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="h-10 pl-9"
                data-testid="search-enrollments-input"
              />
            </div>
            <Select value={productFilter} onValueChange={setProductFilter}>
              <SelectTrigger className="h-10 w-full lg:w-52" data-testid="product-filter">
                <Package className="mr-2 h-4 w-4 text-gray-400" />
                <SelectValue placeholder="All Products" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Products</SelectItem>
                {products.map((product) => (
                  <SelectItem key={getProductId(product)} value={getProductId(product)}>
                    {product.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-10 w-full lg:w-44" data-testid="status-filter">
                <SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
      >
        {loading || filteredEnrollments.length > 0 ? (
          <TailAdminDataTable
            loading={loading}
            columns={["Phone", "Product", "BRM", "Status", "Enrolled", "Subscribed"]}
            rows={rows}
          />
        ) : (
          <TailAdminEmptyState
            icon={FileText}
            title={search ? "No enrollments match your search" : "No enrollments found"}
            description="Enrollments will appear here as BRMs submit proof requests."
          />
        )}
      </TailAdminPanel>
    </div>
  );
};

export default ClientEnrollments;
